import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  itsmApi,
  type ItsmCollection, type ItsmItem, type OpsSchedule, type OpsScheduleCalendar,
} from '../../services/api';

/**
 * A recurring operational task (PO-84 EP-3 FE-4): define its schedule and see its runs.
 *
 * The harness scheduler fires the schedule while the task is IN PROGRESS, and only an
 * approval puts it there. Saving a change to what runs or how often sends it back for
 * approval; switching it off takes effect at once. Runs are read through harness-api
 * and are not work items.
 */
export default function OpsSchedulePanel({ collection, item, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  onChanged: (updated?: ItsmItem) => void;
}) {
  const [cal, setCal] = useState<OpsScheduleCalendar | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OpsSchedule | null>(null);
  const [inputsText, setInputsText] = useState('{}');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setCal(await itsmApi.schedule(collection, item._id));
    } catch { setCal(null); }
  }, [collection, item._id]);

  useEffect(() => { load(); }, [load, item.status, item.updatedAt]);

  const startEdit = () => {
    const s = cal?.schedule || {
      verb: cal?.verbs[0] || '', target: cal?.targets[0] || 'ops-runner',
      inputs: {}, every_minutes: 60, enabled: true,
    };
    setDraft({ ...s });
    setInputsText(JSON.stringify(s.inputs || {}, null, 2));
    setEditing(true);
  };

  const save = async (schedule: OpsSchedule | null) => {
    setSaving(true);
    try {
      const customFields = { ...(item.customFields || {}) };
      if (schedule) customFields.schedule = schedule; else delete customFields.schedule;
      const before = item.status;
      const updated = await itsmApi.update(collection, item._id, { customFields });
      if (before === 'in-progress' && updated.status === 'awaiting-approval') {
        toast('Saved. The change needs approval before the schedule runs again.', { duration: 8000 });
      } else {
        toast.success('Schedule saved');
      }
      setEditing(false);
      onChanged(updated);
    } catch (err) {
      toast.error((err as Error).message || 'Could not save the schedule', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const submit = () => {
    if (!draft) return;
    let inputs: Record<string, unknown>;
    try {
      inputs = JSON.parse(inputsText || '{}');
    } catch {
      toast.error('Inputs must be JSON');
      return;
    }
    save({ ...draft, inputs });
  };

  const sched = cal?.schedule;
  const field = 'w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';

  return (
    <div>
      <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <CalendarClock size={12} /> Schedule
        {sched && (
          <span className={`ml-auto text-[10px] ${cal?.active ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-500'}`}>
            {cal?.active ? 'running' : !sched.enabled ? 'switched off'
              : item.status === 'awaiting-approval' ? 'awaiting approval' : 'not approved'}
          </span>
        )}
      </h3>

      {sched && item.status === 'awaiting-approval' && (
        <p className="mb-2 text-[11px] text-gray-500 dark:text-gray-400">
          Waiting in the <Link to="/approvals" className="text-accent hover:underline">Approvals inbox</Link>:
          one approval puts it in progress.
        </p>
      )}

      {!editing && !sched && (
        <button onClick={startEdit} className="text-xs text-accent">+ Make this recurring</button>
      )}

      {!editing && sched && (
        <div className="space-y-2">
          <p className="text-xs text-gray-700 dark:text-gray-200">
            <span className="font-mono">{sched.verb}</span> on {sched.target}, every {sched.every_minutes} min
            {cal?.nextSlot && (
              <span className="block text-[11px] text-gray-400">
                next about {new Date(cal.nextSlot).toLocaleString()}
              </span>
            )}
          </p>
          <div className="flex gap-3">
            <button onClick={startEdit} className="text-xs text-accent">Edit</button>
            {sched.enabled ? (
              <button disabled={saving} onClick={() => save({ ...sched, enabled: false })}
                className="text-xs text-gray-500 hover:text-red-500">Switch off</button>
            ) : (
              <button disabled={saving} onClick={() => save({ ...sched, enabled: true })}
                className="text-xs text-gray-500 hover:text-accent">Switch on (needs approval)</button>
            )}
          </div>
        </div>
      )}

      {editing && draft && (
        <div className="space-y-2">
          <select value={draft.verb} onChange={(e) => setDraft({ ...draft, verb: e.target.value })} className={field}>
            {(cal?.verbs || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            every
            <input type="number" min={cal?.minEveryMinutes || 5} value={draft.every_minutes}
              onChange={(e) => setDraft({ ...draft, every_minutes: parseInt(e.target.value, 10) || 0 })}
              className={`${field} w-24`} />
            minutes
            <label className="ml-auto flex items-center gap-1">
              <input type="checkbox" checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> enabled
            </label>
          </div>
          <textarea value={inputsText} onChange={(e) => setInputsText(e.target.value)} rows={4}
            className={`${field} font-mono text-xs`} placeholder='{"source": "..."}' />
          <p className="text-[11px] text-gray-400">
            Saving a change to what runs or how often sends it back for approval.
          </p>
          <div className="flex gap-3">
            <button disabled={saving} onClick={submit} className="text-xs text-accent font-medium">Save</button>
            <button onClick={() => setEditing(false)} className="text-xs text-gray-500">Cancel</button>
            {sched && (
              <button disabled={saving} onClick={() => save(null)}
                className="ml-auto text-xs text-gray-500 hover:text-red-500">Remove schedule</button>
            )}
          </div>
        </div>
      )}

      {sched && cal && (
        <div className="mt-3">
          {cal.runsError && (
            <p className="text-[11px] text-amber-600 dark:text-amber-500 mb-1">
              Runs unavailable: {cal.runsError}
            </p>
          )}
          <div className="space-y-1">
            {cal.days.map((d) => (
              <div key={d.date} className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-[10px] font-mono text-gray-400">{d.date.slice(5)}</span>
                <div className="flex-1 flex flex-wrap gap-px min-h-[10px]">
                  {d.runs.map((r) => (
                    <span key={r.id}
                      title={`${r.started_at || r.slot} · ${r.status}${r.duration_s != null ? ` · ${r.duration_s}s` : ''}${r.error ? ` · ${r.error}` : ''}`}
                      className={`w-1.5 h-2.5 rounded-sm ${
                        d.failed && ['failed', 'error', 'timeout', 'refused'].includes(r.status) ? 'bg-red-500'
                          : ['ok', 'done', 'succeeded', 'success'].includes(r.status) ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'}`} />
                  ))}
                </div>
                <span className="w-20 shrink-0 text-right text-[10px] text-gray-400">
                  {d.runs.length}/{d.expected}{d.failed ? <span className="text-red-500"> · {d.failed}✕</span> : null}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-gray-400">Runs per day against the cadence (UTC days).</p>
        </div>
      )}
    </div>
  );
}
